import { BadgeCheck } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * Mirror of `InboundVerificationStatus` from the API. Kept inline (not
 * imported from the generated client) so this component can be reused
 * by surfaces that don't carry the full inbound type — e.g. Gmail rows
 * which currently always pass `null`. Server-side definition lives in
 * `lib/db/src/schema/inbound_emails.ts`.
 */
export type VerificationStatus =
  | "bimi"
  | "dmarc"
  | "unverified"
  | "fail"
  | null
  | undefined;

interface VerifiedSenderBadgeProps {
  /**
   * The cryptographic verification status the API stamped on the
   * inbound row at ingest. The badge renders nothing for
   * `unverified`, `fail`, or absent values — silent absence keeps
   * call-sites able to drop this in unconditionally.
   */
  verificationStatus: VerificationStatus;
  /**
   * Optional extra classes for the icon (e.g. to tweak size on a
   * particular surface). Defaults to a 14×14 inline-friendly size.
   */
  className?: string;
  /**
   * Optional test-id suffix so tests can pin a badge to a specific
   * surface (e.g. inbox row, detail header). Final test id is
   * `verified-sender-badge${testIdSuffix}`.
   */
  testIdSuffix?: string;
}

/**
 * Small check that appears next to a sender's name when the inbound
 * passed real protocol verification at ingest:
 *
 *  - `bimi`  → amber check + "Verified brand (BIMI)" tooltip. The
 *              sender's domain published a BIMI record AND the
 *              message DMARC-aligned to that domain.
 *  - `dmarc` → blue check + "DMARC-aligned sender" tooltip. The
 *              receiving MTA recorded `dmarc=pass` and the result's
 *              `header.from` aligned with the displayed sender.
 *  - `fail`  → renders nothing here. A separate `FailedVerificationWarning`
 *              component handles this case with a subtle muted pill.
 *  - everything else → renders nothing. We don't draw a negative
 *              "unverified" badge because that would punish every
 *              perfectly legitimate small business that simply hasn't
 *              published a DMARC record.
 *
 * The verification itself is computed server-side in
 * `artifacts/api-server/src/lib/senderVerification.ts` and persisted
 * on `inbound_emails.verification_status`.
 */
export function VerifiedSenderBadge({
  verificationStatus,
  className,
  testIdSuffix = "",
}: VerifiedSenderBadgeProps) {
  if (verificationStatus !== "bimi" && verificationStatus !== "dmarc") {
    return null;
  }

  const isBimi = verificationStatus === "bimi";
  const tooltip = isBimi ? "Verified brand (BIMI)" : "DMARC-aligned sender";
  const ariaLabel = tooltip;
  const colorClass = isBimi
    ? "text-amber-500 dark:text-amber-400"
    : "text-blue-600 dark:text-blue-400";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "inline-flex items-center shrink-0 cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm",
            colorClass,
          )}
          data-testid={`verified-sender-badge${testIdSuffix}`}
          data-verification-status={verificationStatus}
          aria-label={ariaLabel}
          role="img"
          tabIndex={0}
        >
          <BadgeCheck
            className={cn("h-3.5 w-3.5", className)}
            fill="currentColor"
            stroke="white"
            strokeWidth={2}
          />
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
